import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface AccessibilitySettings {
  largerText: boolean;
  lineHeight: boolean;
  textAlignment: boolean;
  readableFont: boolean;
  contrast: boolean;
  grayscale: boolean;
  hideImages: boolean;
  reduceAnimations: boolean;
}

const defaultSettings: AccessibilitySettings = {
  largerText: false,
  lineHeight: false,
  textAlignment: false,
  readableFont: false,
  contrast: false,
  grayscale: false,
  hideImages: false,
  reduceAnimations: false,
};

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  toggleSetting: (key: keyof AccessibilitySettings) => void;
  resetSettings: () => void;
  getActiveSettingsCount: () => number;
  getActiveSettingsList: () => { key: keyof AccessibilitySettings; label: string }[];
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

const STORAGE_KEY = 'accessibility-settings';

const settingLabels: Record<keyof AccessibilitySettings, string> = {
  largerText: 'Größerer Text',
  lineHeight: 'Zeilenhöhe',
  textAlignment: 'Textausrichtung',
  readableFont: 'Lesbare Schrift',
  contrast: 'Kontrast',
  grayscale: 'Graustufen',
  hideImages: 'Bilder ausblenden',
  reduceAnimations: 'Animationen reduzieren',
};

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const merged = { ...defaultSettings, ...JSON.parse(stored) };
        return {
          largerText: !!merged.largerText,
          lineHeight: !!merged.lineHeight,
          textAlignment: !!merged.textAlignment,
          readableFont: !!merged.readableFont,
          contrast: !!merged.contrast,
          grayscale: !!merged.grayscale,
          hideImages: !!merged.hideImages,
          reduceAnimations: !!merged.reduceAnimations,
        };
      }
    } catch (e) {
      console.error('Failed to load accessibility settings:', e);
    }
    return defaultSettings;
  });

  useEffect(() => {
    const html = document.documentElement;

    html.classList.toggle('a11y-larger-text', settings.largerText);
    html.classList.toggle('a11y-line-height', settings.lineHeight);
    html.classList.toggle('a11y-text-alignment', settings.textAlignment);
    html.classList.toggle('a11y-readable-font', settings.readableFont);
    html.classList.toggle('a11y-contrast', settings.contrast);
    html.classList.toggle('a11y-grayscale', settings.grayscale);
    html.classList.toggle('a11y-hide-images', settings.hideImages);
    html.classList.toggle('a11y-reduce-animations', settings.reduceAnimations);
    html.classList.remove('a11y-easy-language');

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save accessibility settings:', e);
    }
  }, [settings]);

  const toggleSetting = (key: keyof AccessibilitySettings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const getActiveSettingsCount = () => {
    return Object.values(settings).filter(Boolean).length;
  };

  const getActiveSettingsList = () => {
    return Object.entries(settings)
      .filter(([_, value]) => value)
      .map(([key]) => ({
        key: key as keyof AccessibilitySettings,
        label: settingLabels[key as keyof AccessibilitySettings],
      }));
  };

  return (
    <AccessibilityContext.Provider
      value={{ settings, toggleSetting, resetSettings, getActiveSettingsCount, getActiveSettingsList }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}
